import { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeExternalLinks from 'rehype-external-links';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { LoadingBlock } from '@dbt-labs/sourdough';

import { RuleSeverityEnum } from '@/__generated__/graphql';
import { useRecommendations } from '@/hooks/discovery/recommendations';
import { ArrElement } from '@/typings/arrayElement';
import { toTitleCase } from '@/util/string';
import { formatDisplayedName } from './ruleInfo';
import { RuleSeverityBadge } from './RuleSeverityCell';

export type TableData = ArrElement<ReturnType<typeof useRecommendations>['data']>;

type RecommendationsListItemProps = TableData & { skeleton: true };
const SKELETON_HEIGHT_PX = 20.0;

const RecommendationsListItem: FC<RecommendationsListItemProps> = ({
  name,
  severity,
  violationId,
  category,
  description,
  skeleton,
}) => {
  if (!skeleton && !violationId) return null;

  return (
    <li
      aria-label={name}
      className="overflow-hidden rounded-lg border border-borderMuted bg-bgMain px-4 py-2 text-sm"
    >
      <div className="flex">
        <span className="flex-0">
          {skeleton && (
            <LoadingBlock
              className="mb-2 mr-2"
              width={60}
              height={SKELETON_HEIGHT_PX}
            />
          )}
          {!skeleton && (
            <RuleSeverityBadge severity={severity as RuleSeverityEnum} size="small" />
          )}
        </span>
        {skeleton && (
          <div className="w-1/2">
            <LoadingBlock width={-1} height={SKELETON_HEIGHT_PX} />
          </div>
        )}
        {!skeleton && (
          <>
            <span className="flex-0 not-sr-only ml-2 mr-1 font-semibold leading-6 text-fgMain">
              {formatDisplayedName(category.toLocaleLowerCase())}:
            </span>
            <span className="not-sr-only flex-1 font-light leading-6 text-fgMain">
              {name
                .toLowerCase()
                .split('_')
                .map((x: string) => toTitleCase(x))
                .join(' ')}
            </span>
          </>
        )}
      </div>
      {skeleton && (
        <div className="flex-grow">
          <LoadingBlock width={-1} height={SKELETON_HEIGHT_PX + 30} />
        </div>
      )}
      {!skeleton && description && (
        <ReactMarkdown
          className="prose prose-sm mt-2 w-full min-w-full text-fgDecorative dark:prose-invert"
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, [rehypeExternalLinks, { target: '_blank' }]]}
        >
          {description}
        </ReactMarkdown>
      )}
    </li>
  );
};

export type RecommendationsListProps = {
  recommendations: TableData[];
  isLoading: boolean;
};

export const RecommendationsList: FC<RecommendationsListProps> = ({
  recommendations,
  isLoading,
}) => {
  const items: RecommendationsListItemProps[] = isLoading
    ? Array.from({ length: 5 }).map(
        (_, i) =>
          ({
            violationId: i.toString(),
            skeleton: true,
          }) as any,
      )
    : [...recommendations];

  return (
    <ul className="flex flex-col space-y-2">
      {items.map((v) => (
        <RecommendationsListItem key={v.violationId} {...v} />
      ))}
    </ul>
  );
};
